import { BufferControllerEvents } from '@bufferController/BufferController.events';
import { IBufferMetrics } from '@bufferController/BufferMetrics/IBufferMetrics';
import { TEventBus } from '@eventBus/EventBus';
import { IResolution } from '@resolutionsManager/IResolutionsManager';
import { ResolutionsManagerEvents } from '@resolutionsManager/ResolutionsManager.events';
import bind from '@utils/decorators/bind';

export interface IDroppedFramesHistoryItem {
  totalFramesNumber: number;
  droppedFramesNumber: number;
}

export class DroppedFramesHistory {
  public _eventBus: TEventBus;
  private _droppedFrames: Record<string, IDroppedFramesHistoryItem> = {};
  private _currentResolution: IResolution | null = null;
  private _isFirstMetricsOfResolution: boolean = true;
  private readonly _onBufferMetricsReadyListener: string;
  private readonly _onResolutionChangedListener: string;

  constructor(eventBus: TEventBus) {
    this._eventBus = eventBus;
    this._onBufferMetricsReadyListener = this._eventBus.subscribe(
      BufferControllerEvents.bufferMetricsAvailable,
      this._onBufferMetricsReady,
    );
    this._onResolutionChangedListener = this._eventBus.subscribe(ResolutionsManagerEvents.resolutionChanged, this._onResolutionChanged);
  }

  public destroy(): void {
    this._eventBus.unsubscribe(this._onBufferMetricsReadyListener);
    this._eventBus.unsubscribe(this._onResolutionChangedListener);
    this.reset();
  }

  public getCurrentResolutionHistory(): IDroppedFramesHistoryItem | null {
    if (!this._currentResolution || !this._droppedFrames[this._currentResolution.name]) {
      return null;
    }

    return this._droppedFrames[this._currentResolution.name];
  }

  public getResolutionHistoryByName(name: string): IDroppedFramesHistoryItem | null {
    return this._droppedFrames[name] || null;
  }

  public reset(): void {
    this._droppedFrames = {};
    this._currentResolution = null;
    this._isFirstMetricsOfResolution = true;
  }

  @bind
  private _onBufferMetricsReady(metrics: IBufferMetrics): void {
    if (!this._currentResolution) {
      return;
    }

    const { name } = this._currentResolution;

    if (!this._droppedFrames[name]) {
      this._droppedFrames[name] = {
        droppedFramesNumber: 0,
        totalFramesNumber: 0,
      };
    }

    if (this._isFirstMetricsOfResolution) {
      this._isFirstMetricsOfResolution = false;
      return;
    }

    this._droppedFrames[name].droppedFramesNumber += metrics.droppedVideoFramesDelta;
    this._droppedFrames[name].totalFramesNumber += metrics.totalVideoFramesDelta;
  }

  @bind
  private _onResolutionChanged(resolution: IResolution): void {
    if (this._currentResolution?.name === resolution.name) {
      return;
    }

    this._currentResolution = resolution;
    this._isFirstMetricsOfResolution = true;
  }
}
